import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export default function WeatherPanel() {
  const { t } = useTranslation();
  const [location, setLocation] = useState("");
  const [weather, setWeather] = useState(null);
  const [inputCity, setInputCity] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedCity = localStorage.getItem("weatherCity");
    if (savedCity) {
      setLocation(savedCity);
      fetchWeather(savedCity);
    } else {
      setLocation("Sofia");
      fetchWeather("Sofia");
    }
  }, []);

  const fetchWeather = async (city) => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`/api/weather?location=${city}`);
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || `HTTP error! status: ${res.status}`);
      }
      
      if (!data || !data.current) {
        throw new Error("Weather API returned incomplete data");
      }
      
      setWeather(data);
      setError(null);
    } catch (error) {
      console.error("Failed to fetch weather:", error);
      setError(error.message);
      setWeather(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCitySubmit = (e) => {
    e.preventDefault();
    if (inputCity.trim()) {
      localStorage.setItem("weatherCity", inputCity.trim());
      setLocation(inputCity.trim());
      fetchWeather(inputCity.trim());
      setInputCity("");
    }
  };

  const retryWeather = () => {
    if (location) {
      fetchWeather(location);
    }
  };

  return (
    <div className="w-full h-full bg-white rounded-lg shadow p-6 flex flex-col justify-between border border-gray-200">

      <form onSubmit={handleCitySubmit} className="flex gap-2 mb-6">
        <input
          type="text"
          placeholder={t('weatherPanel.enterCity')}
          value={inputCity}
          onChange={(e) => setInputCity(e.target.value)}
          className="border border-gray-300 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-[#173861] focus:outline-none text-lg"
        />
        <button
          type="submit"
          className="bg-[#173861] hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold shadow"
        >
          {t('weatherPanel.set')}
        </button>
      </form>

      {loading ? (
        <div className="text-center text-gray-500 text-lg">{t('weatherPanel.loadingWeather')}</div>
      ) : error ? (
        <div className="text-center text-red-600 text-lg">
          <div className="mb-4">
            <p className="font-semibold">{t('weatherPanel.weatherDataUnavailable')}</p>
            <p className="text-sm text-gray-600 mt-1">{error}</p>
          </div>
          <button
            onClick={retryWeather}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
          >
            {t('weatherPanel.retry')}
          </button>
        </div>
      ) : !weather ? (
        <div className="text-center text-gray-500 text-lg">{t('weatherPanel.noWeatherData')}</div>
      ) : (
        <div className="flex flex-col justify-between h-full">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-bold text-[#173861]">{t('weatherPanel.currentWeather')}</h2>
              <p className="text-base text-gray-600">{weather.location}</p>
            </div>
            <div className="flex items-center gap-2">
              {weather.current?.icon && (
                <img src={weather.current.icon} alt="weather icon" className="w-16 h-16" />
              )}
              <div className="text-2xl font-bold text-[#173861]">{weather.current.temp_c} {t('weatherPanel.celsius')}</div>
            </div>
          </div>
          <div className="text-base text-gray-600 mb-4">
            {weather.current.condition} · {t('weatherPanel.wind')}: {weather.current.wind_kph} {t('weatherPanel.kmh')} · {t('weatherPanel.humidity')}: {weather.current.humidity}%
          </div>

          <div className="overflow-x-auto">
            <div className="flex gap-6">
              {weather.forecast?.map((day) => (
                <div key={day.date} className="flex flex-col items-center min-w-[120px] bg-gray-50 rounded-lg p-2 shadow-sm">
                  <div className="text-xs text-gray-500 mb-1">{day.date}</div>
                  {day.icon && <img src={day.icon} alt="icon" className="w-12 h-12 mb-1" />}
                  <div className="text-xs text-gray-700 text-center mb-1">{day.condition}</div>
                  <div className="text-xs text-gray-600">
                    {day.min_temp}° / {day.max_temp}°
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}